import { useRef } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Info } from "lucide-react";
import { useTranslate } from "@/lib/i18n";
import { AuthShell, LoginIllustration } from "./login/AuthShell";
import {
  DRIVER_INVITE_NO_APP_PARAM,
  driverAppInviteLinkFor,
  driverInviteFallbackUrl,
} from "./driverAppLinks";

/**
 * THE PAGE A DRIVER'S INVITE LINK OPENS (driver backend B1, plan D-3/D-5).
 *
 * `https://restaurant.<base>/:locale/driver-invite/:token` is what the
 * restaurant shares from its own WhatsApp, because WhatsApp does not make a
 * `trioty-driver://` link tappable and verified App Links / Universal Links
 * wait for a store listing (backlog). The page only hands over to the app:
 * one button whose href is the Android `intent://` URL or the iOS scheme
 * (`driverAppLinks.ts`), and a line for when nothing opens.
 *
 * On Android the intent carries `S.browser_fallback_url` = this page with
 * `?noapp=1`, so a phone without the app lands back here and is told how to
 * get it. No download URL is configured anywhere (the app has no store
 * listing yet), so that copy sends the driver to the restaurant for the app —
 * never to a placeholder link.
 *
 * It reads NO session and calls NO API — it sits outside `ProtectedLayout`,
 * beside the login — so the token goes nowhere but into the app's own link.
 * `no-referrer` keeps it out of any request the page might still make (React
 * hoists the `<meta>` into the document head). The app reads the invite
 * itself (contract #25) once it opens.
 */
export function DriverInviteOpenPage() {
  const t = useTranslate();
  const { locale = "ar", token = "" } = useParams();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const noApp = searchParams.get(DRIVER_INVITE_NO_APP_PARAM) === "1";
  const subtitleRef = useRef<HTMLSpanElement>(null);
  const href = driverAppInviteLinkFor(
    token,
    typeof navigator === "undefined" ? "" : navigator.userAgent,
    typeof window === "undefined"
      ? null
      : driverInviteFallbackUrl(`${window.location.origin}${pathname}`),
  );
  const Arrow = locale === "ar" ? ArrowLeft : ArrowRight;

  return (
    <AuthShell subtitleRef={subtitleRef}>
      <meta name="referrer" content="no-referrer" />
      <span className="auth-hero auth-narrow-only" style={{ marginInlineStart: -14 }}>
        <LoginIllustration id="rider" size={132} />
      </span>
      <h1 className="auth-title">
        {noApp
          ? t((m) => m.driverAccess.inviteNoAppTitle)
          : t((m) => m.driverAccess.inviteTitle)}
      </h1>
      <span ref={subtitleRef} className="auth-sub">
        {noApp
          ? t((m) => m.driverAccess.inviteNoAppBody)
          : t((m) => m.driverAccess.inviteBody)}
      </span>
      {noApp ? (
        <ol className="auth-sub auth-steps" data-testid="driver-invite-no-app-steps">
          <li>{t((m) => m.driverAccess.inviteNoAppStepAsk)}</li>
          <li>{t((m) => m.driverAccess.inviteNoAppStepInstall)}</li>
          <li>{t((m) => m.driverAccess.inviteNoAppStepReopen)}</li>
        </ol>
      ) : null}
      <div className="auth-form">
        <a
          href={href}
          className="auth-submit"
          rel="noreferrer"
          data-testid="driver-invite-open-app"
        >
          <span>
            {noApp
              ? t((m) => m.driverAccess.inviteNoAppRetry)
              : t((m) => m.driverAccess.inviteOpenApp)}
          </span>
          <Arrow size={16} strokeWidth={2} aria-hidden />
        </a>
      </div>
      {noApp ? null : (
        <div className="auth-foot">
          <Info size={15} strokeWidth={1.8} aria-hidden />
          <span>{t((m) => m.driverAccess.inviteInstallHint)}</span>
        </div>
      )}
      <div className="auth-foot">
        <Info size={15} strokeWidth={1.8} aria-hidden />
        <span>{t((m) => m.driverAccess.inviteKeepPrivate)}</span>
      </div>
    </AuthShell>
  );
}
